import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"
import { ru } from "date-fns/locale"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'dd.MM.yyyy', { locale: ru });
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'dd.MM.yyyy HH:mm', { locale: ru });
}

export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'HH:mm', { locale: ru });
}

/** Календарная дата YYYY-MM-DD в локальной таймзоне (не использовать toISOString() для границ месяца — в UTC+ сдвиг «съедает» последний день). */
export function formatLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Convert an ISO/UTC date string to a value suitable for <input type="datetime-local"> (local timezone components). */
export function toDatetimeLocalValue(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Извлекает координаты (latitude, longitude) из ссылки на карту.
 * Поддерживает: Яндекс.Карты, Google Maps, 2ГИС.
 * @returns { latitude, longitude } или null если не удалось извлечь
 */
export function parseGeoFromMapLink(url: string): { latitude: number; longitude: number } | null {
  if (!url?.trim()) return null;
  const s = url.trim();
  try {
    // Яндекс: ll=lon_lat или pt=lon,lat
    const yandexLl = s.match(/[?&]ll=([-\d.]+)_([-\d.]+)/);
    if (yandexLl) {
      const lon = parseFloat(yandexLl[1]);
      const lat = parseFloat(yandexLl[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { latitude: lat, longitude: lon };
      }
    }
    const yandexPt = s.match(/[?&]pt=([-\d.]+),([-\d.]+)/);
    if (yandexPt) {
      const lon = parseFloat(yandexPt[1]);
      const lat = parseFloat(yandexPt[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { latitude: lat, longitude: lon };
      }
    }
    // Google: @lat,lon или !3dLAT!4dLON
    const googleAt = s.match(/@([-\d.]+),([-\d.]+)/);
    if (googleAt) {
      const lat = parseFloat(googleAt[1]);
      const lon = parseFloat(googleAt[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { latitude: lat, longitude: lon };
      }
    }
    const google3d = s.match(/!3d([-\d.]+)!4d([-\d.]+)/);
    if (google3d) {
      const lat = parseFloat(google3d[1]);
      const lon = parseFloat(google3d[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { latitude: lat, longitude: lon };
      }
    }
    // 2ГИС: center=lon,lat
    const dgis = s.match(/center=([-\d.]+),([-\d.]+)/);
    if (dgis) {
      const lon = parseFloat(dgis[1]);
      const lat = parseFloat(dgis[2]);
      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { latitude: lat, longitude: lon };
      }
    }
  } catch {
    return null;
  }
  return null;
}
